// Seeds uploaded before the extension sent a waveform have none. The first
// signed-in viewer to open such a seed's page draws it — decoding the preview
// their browser can already play — and stores it (PUT /seeds/:id/waveform,
// first writer wins). One attempt per seed per page load, and never on the
// feed: a page of cards must not decode twenty tracks.

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { encodeWaveform, peaksOf } from "@shared/audio/waveform.js";
import { previewKeyOf } from "@shared/blobs.js";
import { objectUrl } from "@web-shared/lib/api";
import { json, webApi } from "@web-shared/lib/web-api";
import { useSession } from "./session";

const tried = new Set<string>();

export function useWaveformBackfill(seed: { id: string; waveform?: string | null; masterKey?: string | null }): void {
  const session = useSession();
  const queryClient = useQueryClient();
  const needed = Boolean(session && !seed.waveform && seed.masterKey);

  useEffect(() => {
    if (!needed || !seed.masterKey || tried.has(seed.id)) return;
    tried.add(seed.id);
    const masterKey = seed.masterKey;
    void (async () => {
      const drawn = await drawWaveform(masterKey);
      if (!drawn) return;
      const res = await webApi<{ stored: boolean }>(`/seeds/${encodeURIComponent(seed.id)}/waveform`, json("PUT", drawn));
      if (res.stored) void queryClient.invalidateQueries({ queryKey: ["web"] });
    })().catch((e: unknown) => console.warn("[Seedbed] couldn't draw a waveform:", e));
  }, [needed, seed.id, seed.masterKey, queryClient]);
}

/** Peaks and length of the master: its small MP3 preview when there is one, else the master itself. */
async function drawWaveform(masterKey: string): Promise<{ waveform: string; durationMs: number } | null> {
  const candidates = [previewKeyOf(masterKey), masterKey].filter((k): k is string => k !== null);
  for (const key of candidates) {
    const res = await fetch(objectUrl(key));
    if (!res.ok) continue;
    const ctx = new OfflineAudioContext(1, 1, 44_100);
    const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    const channels = buffer.numberOfChannels;
    const frames = buffer.length;
    const samples = new Float32Array(frames * channels);
    for (let ch = 0; ch < channels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let f = 0; f < frames; f++) samples[f * channels + ch] = data[f]!;
    }
    return { waveform: encodeWaveform(peaksOf({ samples, channels, frames })), durationMs: Math.round(buffer.duration * 1000) };
  }
  return null;
}
